import { Controller } from '@hotwired/stimulus'

// 訪問者属性調査バナー（Issue #12990 / 第2弾 #13379）SP用 Stimulusコントローラー。
//
// 表示制御の方針:
// - 回答済み（localStorageにフラグあり）なら表示しない（ステータスAPIも呼ばない）
// - 未回答なら no_store のステータスAPIで表示可否を確認し、有効なら表示する
//   （対象ページは最大24hキャッシュされるため、サーバー設定のオフを即時反映するためにAPIで確認する）
// - 回答クリック（answer）: localStorageに記録 → 閉じる
//   （GA4送信は各選択肢ボタンの `gtm-tap` クラス＋`data-gtm-event` を既存の汎用GTMタグが拾って行う。
//    このコントローラーでは計測の送信は行わない）
// - 「その他」クリック（showSecondary, #13379）: 記録も閉じもせず、2問目パネルに切り替える。
//   GA4の `visitor_survey_other` 送信はボタンの gtm-tap が担うため、ここでは送信しない。
// - ×クリック: 記録せず閉じる（次回アクセス時に再表示）。2問目表示中の×も同様に未回答扱い。
//
// localStorageが利用不可な環境（プライベートブラウズ厳格モード等）では、
// 読み書きが例外/no-opとなり「未回答」として扱われるため、毎回表示される。

// 回答済みフラグを保存する localStorage キー。
const ANSWERED_STORAGE_KEY = 'visitorSurveyAnswered'

export default class extends Controller {
  static targets = ['modal', 'primary', 'secondary']
  static values = { statusUrl: String }

  connect () {
    // 回答済みなら一切表示せず、APIも呼ばない。
    if (isAnswered()) return

    this.checkStatusAndShow()
  }

  // 要素の除去・ページ遷移等で切断された際に、スクロールロックが残らないよう必ず解除する。
  // unlockScroll() は冪等なので、表示前（lockScroll未実行）に呼ばれても安全。
  disconnect () {
    unlockScroll()
  }

  // サーバー設定値（no_storeのステータスAPI）を確認し、有効ならバナーを表示する。
  async checkStatusAndShow () {
    let enabled = false
    // try は通信・JSONパースのみに限定する（this.show() 内のDOM例外まで握りつぶさないため）。
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[visitor_survey] ステータス取得に失敗しました: ${response.status}`)
        return
      }
      const data = await response.json()
      // 200でも想定外の形（enabled欠落等）なら無言で非表示にせず警告を残す。
      if (typeof data.enabled !== 'boolean') {
        // eslint-disable-next-line no-console
        console.warn('[visitor_survey] ステータス応答の形式が不正です', data)
        return
      }
      enabled = data.enabled
    } catch (e) {
      // 通信失敗時は表示しない（バナー非表示がフェイルセーフ）。原因追跡のため警告は残す。
      // eslint-disable-next-line no-console
      console.warn('[visitor_survey] ステータス取得でエラーが発生しました', e)
      return
    }

    if (enabled) this.show()
  }

  show () {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add(this.visibleClassName)
    lockScroll()
  }

  // 回答を確定したとき（1問目の「探している」「働きたい」、または2問目の6択のいずれか）。
  // GA4送信は `gtm-tap` ＋ `data-gtm-event`（既存の汎用GTMタグ）が担うため、
  // ここでは回答済みの記録とバナーのクローズのみ行う。
  // NOTE: hide() は要素を削除せずCSSで非表示にするだけ（DOMは残る）。2問目の選択肢で answer した場合も
  //       hide()→resetToPrimary() で2問目パネルが display:none になるだけで要素は残る。いずれの経路でも
  //       同一クリックで動くGTMのクリック計測がDOM参照を失わない。hide()/resetToPrimary()をremove()等に変えないこと。
  answer () {
    setAnswered()
    this.hide()
  }

  // 1問目で「その他」を選んだとき（#13379）。回答は確定せず2問目パネルに切り替える。
  // GA4の `visitor_survey_other` 送信はボタンの gtm-tap が担うため、ここでは送信しない。
  showSecondary () {
    if (!this.hasPrimaryTarget || !this.hasSecondaryTarget) return

    this.primaryTarget.style.display = 'none'
    this.secondaryTarget.style.display = 'flex'
  }

  // ×ボタンで閉じたとき（回答とみなさない＝記録しない）。2問目表示中でも同様。
  close () {
    this.hide()
  }

  hide () {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove(this.visibleClassName)
    }
    // 次回表示に備えて1問目から始まる状態へ戻す（CSS既定: 1問目=表示 / 2問目=非表示）。
    this.resetToPrimary()
    unlockScroll()
  }

  // パネル表示を1問目（初期状態）へ戻す。inline styleを解除しSCSSの既定値に戻す。
  resetToPrimary () {
    if (this.hasPrimaryTarget) this.primaryTarget.style.display = ''
    if (this.hasSecondaryTarget) this.secondaryTarget.style.display = ''
  }

  // 表示状態を示すCSSクラス。
  // postcss-modules により component.scss のクラスは
  // `viewcomponent::site::parts::visitor_survey::<mobile|desktop>::component::<name>` にスコープされる。
  // ルート要素の data-classname には末尾 `::component` までのプレフィックス（ViewComponentのcss_root_selector）が
  // 入るため、`::visible` を付与した完全修飾クラス名を生成する（Stimulus識別子とは別物）。
  get visibleClassName () {
    return `${this.element.dataset.classname}::visible`
  }
}

// localStorageに回答済みフラグがあるか。利用不可・例外時は false（未回答扱い）。
function isAnswered () {
  try {
    return window.localStorage.getItem(ANSWERED_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

// 回答済みフラグを保存する。利用不可・例外時は無視（次回も表示される）。
function setAnswered () {
  try {
    window.localStorage.setItem(ANSWERED_STORAGE_KEY, '1')
  } catch (e) {
    // 保存失敗時は次回も表示される（フェイルセーフ）。恒常的に失敗していると毎回表示されるため警告を残す。
    // eslint-disable-next-line no-console
    console.warn('[visitor_survey] 回答済みフラグの保存に失敗しました', e)
  }
}

function lockScroll () {
  document.body.style.overflow = 'hidden'
}

function unlockScroll () {
  document.body.style.overflow = ''
}
